import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, forkJoin, map, of, switchMap, tap } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  buildPlanV2IssueKey,
  PlanV2IssueCardPayload,
  PlanV2IssueDispositionRecord,
  PlanV2IssueQueue,
  PlanV2WorkItemsUrlGroup,
  resolvePlanV2IssueQueue
} from '../utils/plan-v2-issues';
import type { ActionPlanInternalLinkRow } from './inventory-plan-clusters.service';
import type { SupportingPagesV2RowResult } from './dataforseo';
import {
  buildInternalLinkRowLookup,
  collectFocalUrlsWithSectionGapWorkItems,
  collectFreshnessWorkItemUrls,
  repairFreshnessWorkItemRecord,
  repairSupportingSectionWorkItemRecord,
  splitInternalLinkWorkItemForRepair,
  type FreshnessWorkItemContext
} from '../utils/plan-v2-work-item-compose';
import { sortWorkItemsByUrlGroups } from '../utils/plan-v2-work-item-display';

interface PlanV2IssuesListResponse {
  success: boolean;
  data: {
    domain: string;
    items: PlanV2IssueDispositionRecord[];
  };
}

interface PlanV2IssuePatchResponse {
  success: boolean;
  data: {
    domain: string;
    issueKey: string;
    queue: PlanV2IssueQueue;
    item: PlanV2IssueDispositionRecord | null;
  };
}

interface PlanV2RegenerateResponse {
  success: boolean;
  data: {
    domain: string;
    clearedCount: number;
  };
}

@Injectable({ providedIn: 'root' })
export class PlanV2IssueQueueService {
  private readonly API_URL = environment.apiUrl + '/v1';
  private readonly recordsSubject = new BehaviorSubject<Map<string, PlanV2IssueDispositionRecord>>(new Map());
  private readonly exitingSubject = new BehaviorSubject<{ issueKey: string; kind: 'work_item' | 'dismissed' } | null>(
    null
  );
  /** Matches `plan-v2-card-exit-*` CSS duration so the card stays mounted through the animation. */
  private readonly exitAnimationMs = 240;
  private exitStartedAt = 0;
  private activeDomain = '';

  readonly records$ = this.recordsSubject.asObservable();
  readonly exiting$ = this.exitingSubject.asObservable();

  get records(): Map<string, PlanV2IssueDispositionRecord> {
    return this.recordsSubject.value;
  }

  get activeScopeDomain(): string {
    return this.activeDomain;
  }

  constructor(private readonly http: HttpClient) {}

  load(domain: string): Observable<PlanV2IssuesListResponse> {
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    this.activeDomain = scopeDomain;
    const params = new URLSearchParams({ domain: scopeDomain });
    return this.http
      .get<PlanV2IssuesListResponse>(`${this.API_URL}/urls/action-plan/v2/issues?${params}`)
      .pipe(
        tap((res) => {
          const map = new Map<string, PlanV2IssueDispositionRecord>();
          for (const item of res?.data?.items ?? []) {
            map.set(item.issueKey, item);
          }
          this.recordsSubject.next(map);
        })
      );
  }

  getQueue(issueKey: string): PlanV2IssueQueue {
    return resolvePlanV2IssueQueue(issueKey, this.records);
  }

  isVisibleInIssuesEmbed(issueKey: string): boolean {
    return this.getQueue(issueKey) === 'issues';
  }

  workItems(): PlanV2IssueDispositionRecord[] {
    return [...this.records.values()]
      .filter((r) => r.queue === 'work_item')
      .sort((a, b) => (b.priorityScore ?? 0) - (a.priorityScore ?? 0) || a.cardTitle.localeCompare(b.cardTitle));
  }

  workItemsByUrl(): PlanV2WorkItemsUrlGroup[] {
    const byUrl = new Map<string, PlanV2WorkItemsUrlGroup>();
    for (const item of this.workItems()) {
      const urlId = item.urlId.trim();
      const url = item.url.trim();
      if (!urlId || !url) continue;
      let group = byUrl.get(urlId);
      if (!group) {
        group = { urlId, url, priorityScore: item.priorityScore ?? 0, items: [] };
        byUrl.set(urlId, group);
      }
      group.items.push(item);
      group.priorityScore = Math.max(group.priorityScore, item.priorityScore ?? 0);
    }
    return sortWorkItemsByUrlGroups([...byUrl.values()]);
  }

  dismissedCount(): number {
    return [...this.records.values()].filter((r) => r.queue === 'dismissed').length;
  }

  urlIgnoredIssueKey(urlId: string): string {
    return buildPlanV2IssueKey(urlId.trim(), 'url_ignored');
  }

  isUrlIgnored(urlId: string): boolean {
    const hit = this.records.get(this.urlIgnoredIssueKey(urlId));
    return hit?.issueType === 'url_ignored' && hit.queue === 'dismissed';
  }

  ignoredUrlIds(): Set<string> {
    const ids = new Set<string>();
    for (const rec of this.records.values()) {
      if (rec.issueType === 'url_ignored' && rec.queue === 'dismissed') {
        ids.add(rec.urlId);
      }
    }
    return ids;
  }

  ignoreUrl(
    domain: string,
    urlId: string,
    url: string,
    priorityScore?: number | null
  ): Observable<PlanV2IssuePatchResponse> {
    const payload: PlanV2IssueCardPayload = {
      issueKey: this.urlIgnoredIssueKey(urlId),
      urlId: urlId.trim(),
      url: url.trim(),
      issueType: 'url_ignored',
      cardTitle: 'URL ignored',
      changeSummary: 'Hidden from Issues queue until you restore or hard-reset the queue.',
      why: 'Ignored by reviewer.',
      priorityScore: priorityScore ?? null
    };
    return this.setDisposition(domain, payload.issueKey, 'dismissed', payload);
  }

  restoreIgnoredUrl(domain: string, urlId: string): Observable<PlanV2IssuePatchResponse> {
    return this.setDisposition(domain, this.urlIgnoredIssueKey(urlId), 'issues');
  }

  regenerateQueue(domain: string): Observable<PlanV2RegenerateResponse> {
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    return this.http
      .post<PlanV2RegenerateResponse>(`${this.API_URL}/urls/action-plan/v2/issues/regenerate`, {
        domain: scopeDomain
      })
      .pipe(tap(() => this.recordsSubject.next(new Map())));
  }

  beginCardExit(issueKey: string, kind: 'work_item' | 'dismissed'): void {
    this.exitStartedAt = Date.now();
    this.exitingSubject.next({ issueKey, kind });
  }

  clearCardExit(immediate = false): void {
    if (immediate) {
      this.exitingSubject.next(null);
      return;
    }
    const remaining = this.exitAnimationMs - (Date.now() - this.exitStartedAt);
    if (remaining <= 0) {
      this.exitingSubject.next(null);
    } else {
      window.setTimeout(() => this.exitingSubject.next(null), remaining);
    }
  }

  isCardExiting(issueKey: string, kind: 'work_item' | 'dismissed'): boolean {
    const ex = this.exitingSubject.value;
    return Boolean(ex && ex.issueKey === issueKey && ex.kind === kind);
  }

  setDisposition(
    domain: string,
    issueKey: string,
    queue: PlanV2IssueQueue,
    payload?: PlanV2IssueCardPayload
  ): Observable<PlanV2IssuePatchResponse> {
    const scopeDomain = domain.trim().toLowerCase().replace(/^www\./, '');
    return this.http
      .patch<PlanV2IssuePatchResponse>(
        `${this.API_URL}/urls/action-plan/v2/issues/${encodeURIComponent(issueKey)}`,
        {
          domain: scopeDomain,
          queue,
          ...(payload ? { payload } : {})
        }
      )
      .pipe(
        tap((res) => {
          const next = new Map(this.records);
          const item = res?.data?.item;
          if (queue === 'issues' || !item) {
            next.delete(issueKey);
          } else {
            next.set(issueKey, item);
          }
          this.recordsSubject.next(next);
          this.clearCardExit();
        })
      );
  }

  /** Remove the Issues-card row and persist one work item per selected internal link. */
  addInternalLinkWorkItems(
    domain: string,
    issuesCardIssueKey: string,
    payloads: PlanV2IssueCardPayload[]
  ): Observable<number> {
    const valid = payloads.filter((p) => {
      const detail = (p.actionDetail || '').trim();
      return detail && !detail.includes('Re-open the issue and pick links');
    });
    if (!valid.length) {
      return of(0);
    }
    return this.setDisposition(domain, issuesCardIssueKey, 'issues').pipe(
      switchMap(() =>
        forkJoin(
          valid.map((payload) => this.setDisposition(domain, payload.issueKey, 'work_item', payload))
        ).pipe(map((results) => results.length))
      )
    );
  }

  /**
   * Client-side bulk repairs for persisted work items (see docs/TECHNICAL_DEBT.md §0h).
   * Remove once actionDetail is versioned/migrated server-side.
   */
  /** Re-home batched internal-link tasks from beneficiary URLs onto source pages. */
  repairMisplacedInternalLinkWorkItems(
    domain: string,
    linkRows: ActionPlanInternalLinkRow[]
  ): Observable<number> {
    const lookup = buildInternalLinkRowLookup(linkRows);
    const repairs: Array<{ deleteIssueKey: string; replacements: PlanV2IssueCardPayload[] }> = [];
    for (const record of this.records.values()) {
      const split = splitInternalLinkWorkItemForRepair(record, lookup);
      if (split) repairs.push(split);
    }
    if (!repairs.length) {
      return of(0);
    }

    const ops: Observable<PlanV2IssuePatchResponse>[] = [];
    for (const repair of repairs) {
      ops.push(this.setDisposition(domain, repair.deleteIssueKey, 'issues'));
      for (const payload of repair.replacements) {
        ops.push(this.setDisposition(domain, payload.issueKey, 'work_item', payload));
      }
    }
    return forkJoin(ops).pipe(
      map(() => repairs.reduce((count, repair) => count + repair.replacements.length, 0))
    );
  }

  /** Re-label saved section gaps using supporting-pages v2 snapshot paths. */
  repairSupportingSectionWorkItems(
    domain: string,
    v2ByFocalUrl: Map<string, SupportingPagesV2RowResult[]>
  ): Observable<number> {
    const payloads: PlanV2IssueCardPayload[] = [];
    for (const record of this.records.values()) {
      const payload = repairSupportingSectionWorkItemRecord(record, v2ByFocalUrl);
      if (payload) payloads.push(payload);
    }
    if (!payloads.length) {
      return of(0);
    }
    return forkJoin(
      payloads.map((payload) => this.setDisposition(domain, payload.issueKey, 'work_item', payload))
    ).pipe(map((results) => results.length));
  }

  /** Recompose saved freshness cards when SERP band / copy rules change. */
  repairFreshnessWorkItems(
    domain: string,
    contextByUrlId: Map<string, FreshnessWorkItemContext>
  ): Observable<number> {
    const payloads: PlanV2IssueCardPayload[] = [];
    for (const record of this.records.values()) {
      const payload = repairFreshnessWorkItemRecord(record, contextByUrlId);
      if (payload) payloads.push(payload);
    }
    if (!payloads.length) {
      return of(0);
    }
    return forkJoin(
      payloads.map((payload) => this.setDisposition(domain, payload.issueKey, 'work_item', payload))
    ).pipe(map((results) => results.length));
  }

  focalUrlsNeedingSectionGapRepair(): string[] {
    return collectFocalUrlsWithSectionGapWorkItems(this.records);
  }

  freshnessWorkItemUrls(): Array<{ urlId: string; url: string }> {
    return collectFreshnessWorkItemUrls(this.records);
  }
}
